import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../api";
import { Mail, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Always show the same success state regardless of whether the email
    // is actually registered -- Supabase itself never reveals that either,
    // so there's nothing to distinguish here without leaking who has an account.
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    setSent(true);
  };

  return (
    <div style={{ backgroundColor: "#060D1A", minHeight: "100vh" }}
      className="flex items-center justify-center p-4 relative overflow-hidden">

      <div style={{
        position: "absolute", top: "-20%", left: "50%", transform: "translateX(-50%)",
        width: "600px", height: "600px",
        background: "radial-gradient(circle, rgba(0,229,204,0.07) 0%, transparent 65%)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(0,229,204,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,204,0.025) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }} />

      <div className="w-full max-w-sm relative">
        <div style={{
          backgroundColor: "#0D1F3C",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,229,204,0.05)",
        }} className="rounded-3xl p-8">

          <div className="text-center mb-8">
            <img src="/logo192.jpg" alt="FBL Academy"
              style={{ boxShadow: "0 0 24px rgba(0,229,204,0.35)" }}
              className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4" />
            <h1 className="text-xl font-extrabold text-white tracking-tight">Reset your password</h1>
            <p className="text-gray-500 text-sm mt-1">
              {sent ? "Check your inbox" : "We'll email you a reset link"}
            </p>
          </div>

          {sent ? (
            <div className="text-center">
              <div style={{ backgroundColor: "rgba(0,229,204,0.1)", color: "#00E5CC" }}
                className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={22} />
              </div>
              <p className="text-gray-400 text-sm mb-6">
                If an account exists for <span className="text-white font-medium">{email}</span>,
                a password reset link is on its way. It expires soon, so use it shortly.
              </p>
              <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm font-semibold"
                style={{ color: "#00E5CC" }}>
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-gray-400 mb-1.5 block font-medium">Email</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    style={{ backgroundColor: "#080F1E", border: "1px solid rgba(255,255,255,0.08)", color: "white" }}
                    className="w-full rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500/40 transition-all"
                    type="email" placeholder="you@fbl.lk"
                    value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
              </div>

              <button
                style={loading
                  ? { backgroundColor: "rgba(0,229,204,0.4)", color: "#080F1E" }
                  : { background: "linear-gradient(135deg, #00E5CC 0%, #00BFA5 100%)", color: "#080F1E", boxShadow: "0 4px 16px rgba(0,229,204,0.3)" }}
                className="w-full py-3 rounded-xl font-bold text-sm hover:opacity-90 transition-all flex items-center justify-center gap-2 mt-2"
                type="submit" disabled={loading}>
                {loading ? "Sending…" : <><span>Send Reset Link</span><ArrowRight size={15} /></>}
              </button>

              <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 transition-colors pt-1">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
